import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useJobDescriptionState, { IJobDescription } from '@/states/job.state';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import useKeyActivityState from '@/states/key-activities.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useFormContext } from 'react-hook-form';

export default function useJobDescriptionHandlers({ reset }: { reset: any }) {
  const formContext = useFormContext();
  const [selectedRoleInForm, setSelectedRoleInForm] = useState<string | null>(null);

  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IJobDescription | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedKeyActivity, setSelectedKeyActivity] = useState('');

  const [allPublished, setAllPublished] = useState(false);
  const [filteredKeyActivities, setFilteredKeyActivities] = useState<any[]>([]);

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'name',
    order: 'asc',
  });

  useEffect(() => {
    if (!formContext) return;

    const subscription = formContext.watch(values => {
      const roleId = values?.org_chart_node_id || null;
      setSelectedRoleInForm(roleId);
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    if (formMode === 'edit' && selectedRow?.org_chart_node_id) {
      setSelectedRoleInForm(String(selectedRow.org_chart_node_id));
    }
  }, [formMode, selectedRow]);

  const { currentTenant } = useTenantState();

  const {
    jobDescriptions,
    paginatedData,
    getJobDescriptions,
    createJobDescription,
    updateJobDescription,
    deleteJobDescription,
    isLoading,
    bulkPublishJobDescriptions,
  } = useJobDescriptionState();

  const { orgCharts, fetchOrgChartTree } = useOrgChartState();
  const { keyActivities, getKeyActivities } = useKeyActivityState();

  const flattenTree = (nodes: any[]): any[] => {
    const result: any[] = [];
    nodes.forEach(node => {
      result.push(node);
      if (node.children?.length > 0) result.push(...flattenTree(node.children));
    });
    return result;
  };

  const handleAllPublish = async (published: boolean) => {
    if (!currentTenant?.id) return;
    await bulkPublishJobDescriptions(currentTenant.id, published);
  };

  useEffect(() => {
    setAllPublished(jobDescriptions.length > 0 && jobDescriptions.every(item => item.is_published === true));
  }, [jobDescriptions]);

  // Fetch data with server-side pagination and filters
  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      await fetchOrgChartTree(currentTenant.id);
      await getKeyActivities(currentTenant.id, { page: 0, page_size: 9999 }); // Get all key activities for filter
      let publishedFilter: boolean | undefined;
      if (selectedStatus === 'Published') {
        publishedFilter = true;
      } else if (selectedStatus === 'Unpublished') {
        publishedFilter = false;
      }
      await getJobDescriptions(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
        org_key_activity_id: selectedKeyActivity ? Number(selectedKeyActivity) : undefined,
        name: searchTerm || undefined,
        published: publishedFilter,
        orderBy: pageConfig.orderBy,
        order: pageConfig.order,
      });
    } catch {
      toast.error('Failed to fetch data');
    }
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.orderBy,
    pageConfig.order,
    selectedRole,
    selectedKeyActivity,
    searchTerm,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const roleId = selectedRoleInForm || selectedRole;

    if (!roleId) {
      setFilteredKeyActivities([]);
      return;
    }

    const filtered = keyActivities.filter(activity => String(activity.org_chart_node_id) === String(roleId));

    setFilteredKeyActivities(filtered);
  }, [selectedRoleInForm, selectedRole, keyActivities]);

  // Data now comes from server-side pagination
  const displayData = paginatedData || {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
  };

  const handleSave = async (data: IJobDescription) => {
    if (!currentTenant?.id) return;

    try {
      const payload = {
        ...data,
        org_chart_node_id: Number(data.org_chart_node_id),
        org_key_activity_id: Number(data.org_key_activity_id),
        is_published: Boolean(data.is_published),
      };

      if (formMode === 'edit' && selectedRow?.id) {
        await updateJobDescription(currentTenant.id, selectedRow.id, payload);
      } else {
        await createJobDescription(currentTenant.id, payload);
      }

      setFormMode(null);
      setSelectedRow(null);
      reset({});
      await fetchData();
    } catch {
      toast.error('Failed to save job description');
    }
  };

  const handleDelete = async (row: IJobDescription) => {
    if (!currentTenant?.id || !row.id) return;
    await deleteJobDescription(currentTenant.id, row.id);
    await fetchData();
  };

  const allRoles = flattenTree(orgCharts);
  const roleOptions = allRoles.map(n => ({ label: n.name || '-', value: String(n.id) }));

  const keyActivityOptions = keyActivities.map(a => ({
    label: a.name || '-',
    value: String(a.id),
  }));

  return {
    isLoading,
    allPublished,
    formMode,
    orgCharts,
    fields: [
      {
        name: 'org_chart_node_id',
        label: 'Role',
        type: 'select',
        options: roleOptions,
        placeholder: 'Select Role',
        required: true,
      },
      {
        name: 'org_key_activity_id',
        label: 'Key Activity',
        type: 'select',
        options: filteredKeyActivities.map(a => ({
          label: a.name ?? '-',
          value: String(a.id),
        })),
        placeholder: selectedRoleInForm ? 'Select Key Activity' : 'Select Role first',
        disabled: !selectedRoleInForm,
        required: true,
      },
      { name: 'name', label: 'Jobdesc', type: 'textarea', required: true },
      { name: 'frequency', label: 'Frequency', type: 'text' },
      { name: 'report', label: 'Report', type: 'text' },
      { name: 'kpi', label: 'KPI', type: 'text' },
      { name: 'kpi_measurement', label: 'KPI Measurement', type: 'richtext' },
    ],

    displayData,
    pageConfig,
    setPageConfig,
    handleSave,
    handleDelete,
    setFormMode,
    setSelectedRow,
    searchTerm,
    setSearchTerm,
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    selectedKeyActivity,
    setSelectedKeyActivity,
    roleOptions,
    keyActivityOptions,

    handleAllPublish,
    fetchData,
  };
}
